import { useEffect, useLayoutEffect, useRef, useState, ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface DropdownItem {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  /** Separator nad tym itemem. */
  divider?: boolean;
  /** Destruktywne działanie - czerwony styling. */
  destructive?: boolean;
}

export interface DropdownProps {
  trigger: ReactNode;
  items: DropdownItem[];
  /** Wyrównanie menu: do lewej krawędzi triggera albo do prawej. */
  align?: 'start' | 'end';
  className?: string;
  /** Gdy true, menu rozwinięte przyjmuje pełną szerokość wrappera (nie min-w-[10rem]).
   *  Używane w miejscach, gdzie wrapper ma wymuszoną szerokość (np. sidebar). */
  menuFullWidth?: boolean;
  /** Pozycja menu względem triggera. 'bottom' (default) = pod triggerem,
   * 'right' = na prawo (np. dla collapsed sidebar). Patrz `docs/design-system.md`. */
  placement?: 'bottom' | 'right';
  /** Renderuje menu przez createPortal do document.body. Używaj gdy trigger
   * siedzi w kontenerze z `overflow:hidden` lub nisko-z-index'owym scope -
   * inaczej menu jest wycinane lub zakrywane. Pozycja liczona z
   * getBoundingClientRect triggera + listenery na resize/scroll. */
  portal?: boolean;
}

/**
 * Menu dropdown wyzwalane klikiem. Keyboard nav: ↑↓ Enter Esc.
 * Zamyka się na click outside lub Escape. Bez collision detection
 * (MVP bez floating-ui) - używaj align='end' jeśli trigger jest przy
 * prawej krawędzi.
 */
export function Dropdown({ trigger, items, align = 'start', className, menuFullWidth = false, placement = 'bottom', portal = false }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const [focusIdx, setFocusIdx] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  // Portal mode: pozycja menu liczona z getBoundingClientRect triggera.
  const [portalPos, setPortalPos] = useState<{ top: number; left: number; width: number } | null>(null);
  useLayoutEffect(() => {
    if (!portal || !open || !triggerRef.current) return;
    const update = () => {
      const r = triggerRef.current!.getBoundingClientRect();
      if (placement === 'right') {
        setPortalPos({ top: r.top, left: r.right + 6, width: r.width });
      } else {
        setPortalPos({ top: r.bottom + 6, left: r.left, width: r.width });
      }
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [portal, open, placement]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideTrigger = containerRef.current?.contains(target);
      const insideMenu = menuRef.current?.contains(target);
      if (!insideTrigger && !insideMenu) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); return; }
      const enabled = items.map((it, i) => it.disabled ? -1 : i).filter(i => i !== -1);
      if (enabled.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusIdx(prev => {
          const cur = prev == null ? -1 : enabled.indexOf(prev);
          return enabled[(cur + 1) % enabled.length];
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusIdx(prev => {
          const cur = prev == null ? enabled.length : enabled.indexOf(prev);
          return enabled[(cur - 1 + enabled.length) % enabled.length];
        });
      } else if (e.key === 'Enter' && focusIdx != null) {
        e.preventDefault();
        const item = items[focusIdx];
        if (item && !item.disabled) {
          item.onClick?.();
          setOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, items, focusIdx]);

  const menuItems = (
          <>
          {items.map((item, idx) => (
            <div key={item.id}>
              {item.divider && <div className="my-1 h-px bg-border-subtle" />}
              <button
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  if (!item.disabled) {
                    item.onClick?.();
                    setOpen(false);
                  }
                }}
                onMouseEnter={() => setFocusIdx(idx)}
                className={[
                  'w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors',
                  item.disabled
                    ? 'opacity-50 cursor-not-allowed text-text-disabled'
                    : item.destructive
                      ? 'text-danger hover:bg-danger/10'
                      : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover',
                  focusIdx === idx && !item.disabled ? (item.destructive ? 'bg-danger/10' : 'bg-surface-hover text-text-primary') : '',
                ].filter(Boolean).join(' ')}
              >
                {item.icon && (
                  <span className="inline-flex items-center justify-center w-4 h-4 shrink-0 [&>svg]:w-full [&>svg]:h-full" aria-hidden="true">
                    {item.icon}
                  </span>
                )}
                <span className="truncate">{item.label}</span>
              </button>
            </div>
          ))}
          </>
  );

  return (
    <div ref={containerRef} className={['relative inline-block', className].filter(Boolean).join(' ')}>
      <div ref={triggerRef} onClick={() => { setOpen(o => !o); setFocusIdx(null); }}>
        {trigger}
      </div>
      {open && portal && portalPos && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{ position: 'fixed', top: portalPos.top, left: portalPos.left, minWidth: portalPos.width }}
          className="z-[1000] rounded-xl bg-surface-raised border border-border shadow-glass backdrop-blur-xl py-1.5 animate-scale-in min-w-[12rem] origin-top"
        >
          {menuItems}
        </div>,
        document.body,
      )}
      {open && !portal && (
        <div
          ref={menuRef}
          role="menu"
          className={[
            'absolute z-50 rounded-xl bg-surface-raised border border-border shadow-glass backdrop-blur-xl py-1.5 animate-scale-in',
            placement === 'right'
              ? 'left-full top-0 ml-1.5 min-w-[12rem] origin-left'
              : `mt-1.5 origin-top ${menuFullWidth ? 'left-0 right-0 w-full' : `min-w-[10rem] ${align === 'end' ? 'right-0' : 'left-0'}`}`,
          ].join(' ')}
        >
          {items.map((item, idx) => (
            <div key={item.id}>
              {item.divider && <div className="my-1 h-px bg-border-subtle" />}
              <button
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  if (!item.disabled) {
                    item.onClick?.();
                    setOpen(false);
                  }
                }}
                onMouseEnter={() => setFocusIdx(idx)}
                className={[
                  'w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors',
                  item.disabled
                    ? 'opacity-50 cursor-not-allowed text-text-disabled'
                    : item.destructive
                      ? 'text-danger hover:bg-danger/10'
                      : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover',
                  focusIdx === idx && !item.disabled ? (item.destructive ? 'bg-danger/10' : 'bg-surface-hover text-text-primary') : '',
                ].filter(Boolean).join(' ')}
              >
                {item.icon && (
                  <span className="inline-flex items-center justify-center w-4 h-4 shrink-0 [&>svg]:w-full [&>svg]:h-full" aria-hidden="true">
                    {item.icon}
                  </span>
                )}
                <span className="truncate">{item.label}</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
